import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

// ILS monetary pnl values come from server already in shekels (fixIlsPnL); display as whole shekels
const fmt = (n, currency = 'USD') => {
  if (n === undefined || n === null || isNaN(n)) return '-';
  if (currency === 'ILS') {
    try {
      return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n));
    } catch { return `₪${Math.round(n).toLocaleString('he-IL')}`; }
  }
  return new Intl.NumberFormat('he-IL', { style: 'currency', currency, minimumFractionDigits: 2 }).format(n);
};
// ILS per-share prices are in agorot — show as integer agorot
const fmtPrice = (n, currency = 'USD') => {
  if (n === undefined || n === null || isNaN(n)) return '-';
  if (currency === 'ILS') return `${Math.round(n).toLocaleString('he-IL')} אג'`;
  return new Intl.NumberFormat('he-IL', { style: 'currency', currency, minimumFractionDigits: 2 }).format(n);
};
const fmtPct = (n) => (!n && n !== 0) ? '-' : (n >= 0 ? `+${n.toFixed(2)}%` : `${n.toFixed(2)}%`);
const today = () => new Date().toISOString().slice(0, 10);

const COMMISSION_TYPES = [
  { value: 'buy_commission', label: 'עמלת קנייה' },
  { value: 'sell_commission', label: 'עמלת מכירה' },
  { value: 'forex_fee', label: 'עמלת מט"ח' },
  { value: 'management_fee', label: 'דמי ניהול' },
  { value: 'transfer_fee', label: 'עמלת העברה' },
  { value: 'tax', label: 'מס' },
  { value: 'other', label: 'אחר' },
];

const CURRENCIES = [
  { value: 'USD', label: 'דולר (USD)' },
  { value: 'ILS', label: 'שקל (ILS)' },
  { value: 'EUR', label: 'אירו (EUR)' },
  { value: 'GBP', label: 'פאונד (GBP)' },
];

function TransactionModal({ instance, editTx, onClose, onSaved }) {
  const isEdit = !!editTx;
  const [form, setForm] = useState(isEdit
    ? { type: editTx.type, quantity: String(editTx.quantity), price_per_share: String(editTx.price_per_share), currency: editTx.currency || instance.base_currency, transaction_date: (editTx.transaction_date || '').slice(0, 10), notes: editTx.notes || '' }
    : { type: 'buy', quantity: '', price_per_share: '', currency: instance.base_currency, transaction_date: today(), notes: '' });
  const [commissions, setCommissions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const addCommission = () => {
    setCommissions(cs => [...cs, {
      type: form.type === 'buy' ? 'buy_commission' : 'sell_commission',
      amount: '',
      currency: instance.base_currency,
      description: '',
    }]);
  };

  const updateCommission = (i, field, value) =>
    setCommissions(cs => cs.map((c, idx) => idx === i ? { ...c, [field]: value } : c));

  const removeCommission = (i) =>
    setCommissions(cs => cs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/instances/${instance.id}/transactions/${editTx.id}`, form);
        onSaved();
        return;
      }
      await api.post(`/instances/${instance.id}/transactions`, form);
      for (const c of commissions) {
        const amt = parseFloat(c.amount);
        if (!amt || amt === 0) continue;
        await api.post(`/instances/${instance.id}/commissions`, {
          type: c.type,
          amount: amt,
          currency: c.currency,
          commission_date: form.transaction_date,
          description: c.type === 'other' ? (c.description || '') : '',
        });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה');
    } finally {
      setLoading(false);
    }
  };

  const totalRaw = (form.quantity && form.price_per_share) ? parseFloat(form.quantity) * parseFloat(form.price_per_share) : null;
  const total = totalRaw !== null ? (form.currency === 'ILS' ? Math.round(totalRaw / 100) : totalRaw) : null;
  const validCommissions = commissions.filter(c => parseFloat(c.amount) > 0);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{isEdit ? 'עריכת תנועה' : 'הוספת תנועה'} — {instance.stock_symbol}</div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">סוג תנועה</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ v: 'buy', label: '🟢 קנייה' }, { v: 'sell', label: '🔴 מכירה' }].map(({ v, label }) => (
                <button key={v} type="button"
                  onClick={() => {
                    setForm(f => ({...f, type: v}));
                    setCommissions(cs => cs.map(c => ({
                      ...c,
                      type: c.type === 'buy_commission' || c.type === 'sell_commission'
                        ? (v === 'buy' ? 'buy_commission' : 'sell_commission')
                        : c.type
                    })));
                  }}
                  className="btn" style={{ flex: 1, justifyContent: 'center',
                    background: form.type === v ? (v === 'buy' ? 'rgba(0,168,122,.12)' : 'rgba(229,62,94,.12)') : 'var(--surface2)',
                    color: form.type === v ? (v === 'buy' ? 'var(--green)' : 'var(--red)') : 'var(--muted)',
                    border: `1.5px solid ${form.type === v ? (v === 'buy' ? 'var(--green)' : 'var(--red)') : 'var(--border)'}` }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">כמות *</label>
              <input className="form-input" type="number" step="any" min="0" placeholder="0" value={form.quantity}
                onChange={e => setForm({...form, quantity: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">מחיר למניה{instance.base_currency === 'ILS' ? " (אג')" : ''} *</label>
              <input className="form-input" type="number" step="any" min="0" placeholder={instance.base_currency === 'ILS' ? '0' : '0.00'} value={form.price_per_share}
                onChange={e => setForm({...form, price_per_share: e.target.value})} required />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">מטבע</label>
              <select className="form-select" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">תאריך *</label>
              <input className="form-input" type="date" value={form.transaction_date}
                onChange={e => setForm({...form, transaction_date: e.target.value})} required />
            </div>
          </div>
          {total && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: '.9rem' }}>
              סה"כ: <strong>{fmt(parseFloat(total), form.currency)}</strong>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">הערות</label>
            <input className="form-input" placeholder="אופציונלי" value={form.notes}
              onChange={e => setForm({...form, notes: e.target.value})} />
          </div>

          {/* עמלות */}
          {!isEdit && <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: commissions.length ? 10 : 0 }}>
              <span style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--muted)' }}>
                💸 עמלות{commissions.length > 0 ? ` (${commissions.length})` : ''}
              </span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addCommission}>+ הוסף עמלה</button>
            </div>
            {commissions.map((c, i) => (
              <div key={i} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <select className="form-select" style={{ flex: 1 }} value={c.type}
                    onChange={e => updateCommission(i, 'type', e.target.value)}>
                    {COMMISSION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <button type="button" onClick={() => removeCommission(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'var(--red)', padding: '0 4px' }}>✕</button>
                </div>
                {c.type === 'other' && (
                  <input className="form-input" placeholder="תיאור *" style={{ marginBottom: 8 }}
                    value={c.description}
                    onChange={e => updateCommission(i, 'description', e.target.value)} />
                )}
                <div className="form-grid">
                  <input className="form-input" type="number" step="any" min="0" placeholder="סכום"
                    value={c.amount} onChange={e => updateCommission(i, 'amount', e.target.value)} />
                  <select className="form-select" value={c.currency}
                    onChange={e => updateCommission(i, 'currency', e.target.value)}>
                    {CURRENCIES.map(cur => <option key={cur.value} value={cur.value}>{cur.label}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>}

          <div className="modal-actions" style={{ marginTop: 16 }}>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
              {loading ? 'שומר...' : isEdit ? 'שמור שינויים' : validCommissions.length > 0
                ? `הוסף תנועה + ${validCommissions.length} ${validCommissions.length > 1 ? 'עמלות' : 'עמלה'}`
                : 'הוסף תנועה'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CommissionModal({ instance, editCommission, onClose, onSaved }) {
  const isEdit = !!editCommission;
  const [isCredit, setIsCredit] = useState(isEdit ? editCommission.amount < 0 : false);
  const [form, setForm] = useState(isEdit
    ? { type: editCommission.type, description: editCommission.description || '', amount: String(editCommission.amount), currency: editCommission.currency || instance.base_currency, commission_date: (editCommission.commission_date || '').slice(0, 10) }
    : { type: 'buy_commission', description: '', amount: '', currency: instance.base_currency, commission_date: today() });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt === 0) return setError('יש להזין סכום שאינו אפס');
    if (isCredit && amt > 0) return setError('זיכוי חייב להיות סכום שלילי (לדוגמה: -5.00)');
    if (!isCredit && amt < 0) return setError('עמלה חייבת להיות סכום חיובי');
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/instances/${instance.id}/commissions/${editCommission.id}`, { ...form, amount: amt });
      } else {
        await api.post(`/instances/${instance.id}/commissions`, { ...form, amount: amt });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה');
    } finally {
      setLoading(false);
    }
  };

  const handleKindChange = (credit) => {
    setIsCredit(credit);
    setForm(f => ({ ...f, amount: '' }));
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">💸 {isEdit ? 'עריכת עמלה / זיכוי' : 'הוספת עמלה / זיכוי'}</div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          {/* עמלה / זיכוי toggle */}
          <div className="form-group">
            <label className="form-label">סוג רשומה</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ credit: false, label: '💸 עמלה / חיוב' }, { credit: true, label: '✅ זיכוי' }].map(({ credit, label }) => (
                <button key={String(credit)} type="button"
                  onClick={() => handleKindChange(credit)}
                  className="btn"
                  style={{
                    flex: 1, justifyContent: 'center',
                    background: isCredit === credit ? (credit ? 'rgba(0,168,122,.12)' : 'rgba(229,62,94,.12)') : 'var(--surface2)',
                    color: isCredit === credit ? (credit ? 'var(--green)' : 'var(--red)') : 'var(--muted)',
                    border: `1.5px solid ${isCredit === credit ? (credit ? 'var(--green)' : 'var(--red)') : 'var(--border)'}`
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">סוג עמלה *</label>
            <select className="form-select" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              {COMMISSION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {form.type === 'other' && (
            <div className="form-group">
              <label className="form-label">תיאור *</label>
              <input className="form-input" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required />
            </div>
          )}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                סכום * {isCredit && <span style={{ color: 'var(--red)', fontSize: '.8rem' }}>(חייב להיות שלילי)</span>}
              </label>
              <input
                className="form-input"
                type="number"
                step="any"
                {...(isCredit ? { max: 0 } : { min: 0 })}
                placeholder={isCredit ? '-5.00' : '0.00'}
                value={form.amount}
                onChange={e => setForm({...form, amount: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">מטבע</label>
              <select className="form-select" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">תאריך *</label>
            <input className="form-input" type="date" value={form.commission_date}
              onChange={e => setForm({...form, commission_date: e.target.value})} required />
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'שומר...' : isEdit ? 'שמור שינויים' : (isCredit ? 'הוסף זיכוי' : 'הוסף עמלה')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DividendModal({ instance, editDividend, onClose, onSaved }) {
  const isEdit = !!editDividend;
  const [form, setForm] = useState(isEdit
    ? { amount_per_share: editDividend.amount_per_share != null ? String(editDividend.amount_per_share) : '', quantity: editDividend.quantity != null ? String(editDividend.quantity) : '', total_amount: String(editDividend.total_amount), currency: editDividend.currency || instance.base_currency, dividend_date: (editDividend.dividend_date || '').slice(0, 10), notes: editDividend.notes || '', tax_amount: '' }
    : { amount_per_share: '', quantity: '', total_amount: '', currency: instance.base_currency, dividend_date: today(), notes: '', tax_amount: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoCalc, setAutoCalc] = useState(false);

  // Auto-calculate total when amount_per_share and quantity are filled
  useEffect(() => {
    if (autoCalc && form.amount_per_share && form.quantity) {
      const total = parseFloat(form.amount_per_share) * parseFloat(form.quantity);
      if (!isNaN(total)) setForm(f => ({ ...f, total_amount: total.toFixed(4) }));
    }
  }, [form.amount_per_share, form.quantity, autoCalc]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/instances/${instance.id}/dividends/${editDividend.id}`, form);
      } else {
        await api.post(`/instances/${instance.id}/dividends`, form);
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">💰 {isEdit ? 'עריכת דיבידנד' : 'רישום דיבידנד'} — {instance.stock_symbol}</div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={autoCalc} onChange={e => setAutoCalc(e.target.checked)} />
              חישוב אוטומטי (דיב' למניה × כמות)
            </label>
          </div>
          {autoCalc && (
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">דיבידנד למניה</label>
                <input className="form-input" type="number" step="any" min="0" placeholder="0.00"
                  value={form.amount_per_share} onChange={e => setForm({ ...form, amount_per_share: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">כמות מניות</label>
                <input className="form-input" type="number" step="any" min="0" placeholder="0"
                  value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
              </div>
            </div>
          )}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">סה"כ דיבידנד שהתקבל *</label>
              <input className="form-input" type="number" step="any" min="0" placeholder="0.00"
                value={form.total_amount} onChange={e => setForm({ ...form, total_amount: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">מטבע</label>
              <select className="form-select" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">תאריך תשלום *</label>
            <input className="form-input" type="date" value={form.dividend_date}
              onChange={e => setForm({ ...form, dividend_date: e.target.value })} required />
          </div>
          {!isEdit && (
          <div className="form-group">
            <label className="form-label">מס ניכוי במקור <span style={{color:'var(--muted)',fontWeight:400}}>(אופציונלי)</span></label>
            <input className="form-input" type="number" step="any" min="0" placeholder="0.00"
              value={form.tax_amount} onChange={e => setForm({ ...form, tax_amount: e.target.value })} />
            {form.tax_amount && parseFloat(form.tax_amount) > 0 && (
              <div style={{fontSize:'.8rem',color:'var(--muted)',marginTop:4}}>ייווצר אוטומטית כעמלת מס בתאריך הדיבידנד</div>
            )}
          </div>
          )}
          <div className="form-group">
            <label className="form-label">הערות</label>
            <input className="form-input" placeholder="אופציונלי" value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'שומר...' : isEdit ? 'שמור שינויים' : 'הוסף דיבידנד'}</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConversionModal({ instance, editConversion, onClose, onSaved }) {
  const isEdit = !!editConversion;
  const [form, setForm] = useState(isEdit
    ? { from_currency: editConversion.from_currency, to_currency: editConversion.to_currency, amount_from: String(editConversion.amount_from), amount_to: String(editConversion.amount_to), exchange_rate: String(editConversion.exchange_rate), conversion_date: (editConversion.conversion_date || '').slice(0, 10), notes: editConversion.notes || '' }
    : { from_currency: 'USD', to_currency: 'ILS', amount_from: '', amount_to: '', exchange_rate: '', conversion_date: today(), notes: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/instances/${instance.id}/conversions/${editConversion.id}`, form);
      } else {
        await api.post(`/instances/${instance.id}/conversions`, form);
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">💱 {isEdit ? 'עריכת המרת מטבע' : 'המרת מטבע'}</div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">ממטבע</label>
              <select className="form-select" value={form.from_currency} onChange={e => setForm({...form, from_currency: e.target.value})}>
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">למטבע</label>
              <select className="form-select" value={form.to_currency} onChange={e => setForm({...form, to_currency: e.target.value})}>
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">סכום מקור *</label>
              <input className="form-input" type="number" step="any" value={form.amount_from}
                onChange={e => setForm({...form, amount_from: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">סכום יעד *</label>
              <input className="form-input" type="number" step="any" value={form.amount_to}
                onChange={e => setForm({...form, amount_to: e.target.value})} required />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">שער חליפין *</label>
              <input className="form-input" type="number" step="any" placeholder="לדוגמה: 3.65" value={form.exchange_rate}
                onChange={e => setForm({...form, exchange_rate: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">תאריך *</label>
              <input className="form-input" type="date" value={form.conversion_date}
                onChange={e => setForm({...form, conversion_date: e.target.value})} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">הערות</label>
            <input className="form-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'שומר...' : isEdit ? 'שמור שינויים' : 'הוסף המרה'}</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditInstanceModal({ instance, onClose, onSaved }) {
  const [form, setForm] = useState({ 
    stock_symbol: instance.stock_symbol, 
    platform_name: instance.platform_name, 
    notes: instance.notes || '', 
    ticker_symbol: instance.ticker_symbol || '',
    quote_source: instance.quote_source || 'finnhub',
    leverage_factor: instance.leverage_factor || 1,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.patch(`/instances/${instance.id}`, form);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בעדכון');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">עריכת פרטי פוזיציה</div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">שם הנכס *</label>
            <input className="form-input" value={form.stock_symbol} onChange={e => setForm({...form, stock_symbol: e.target.value})} required />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">סימול (Ticker) *</label>
              <input className="form-input" value={form.ticker_symbol} onChange={e => setForm({...form, ticker_symbol: e.target.value.toUpperCase()})} required />
            </div>
            <div className="form-group">
              <label className="form-label">פלטפורמה *</label>
              <input className="form-input" value={form.platform_name} onChange={e => setForm({...form, platform_name: e.target.value})} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">הערות</label>
            <input className="form-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">מקור נתונים</label>
              <select className="form-select" value={form.quote_source} onChange={e => setForm({...form, quote_source: e.target.value})}>
                <option value="finnhub">Finnhub (US)</option>
                <option value="yahoo">Yahoo Finance (TASE)</option>
                <option value="bizportal">Bizportal (קרן TASE — טיקר=מס' נייר)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">מינוף (x)</label>
              <input className="form-input" type="number" min="1" step="0.5" value={form.leverage_factor}
                onChange={e => setForm({...form, leverage_factor: parseFloat(e.target.value) || 1})} />
            </div>
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'מעדכן...' : 'שמור שינויים'}</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LiveData({ instance }) {
  const [live, setLive] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchQuote = async () => {
      if (instance.status !== 'open' || !instance.ticker_symbol) return;
      try {
        const { data } = await api.get(`/instances/${instance.id}/quote`);
        setLive(data);
      } catch (e) {
        setError('שגיאה בטעינת מידע חי');
        console.error(e);
      }
    };
    fetchQuote();
  }, [instance.stock_symbol, instance.status, instance.ticker_symbol]);

  if (instance.status !== 'open') return null;
  if (!instance.ticker_symbol) return <div className="pnl-item pnl-span2" style={{textAlign: 'center', fontSize: '.8rem', padding: '16px'}}>הוסף סימול (Ticker) כדי לראות מידע חי</div>;
  if (error && !live) return <div className="pnl-item pnl-span2 pnl-live-error">{error}</div>;
  if (!live) return <div className="pnl-item pnl-span2" style={{textAlign: 'center', fontSize: '.8rem'}}>טוען מידע חי...</div>;
  
  const pnl = instance.pnl || {};
  // For ILS: live.current is in agorot (×100 from Yahoo); pnl.unrealizedCost is in shekels (fixIlsPnL)
  const ilsFactor = instance.base_currency === 'ILS' ? 100 : 1;
  const currentValue = live.current * (pnl.remainingQty || 0) / ilsFactor; // shekels for ILS
  const unrealizedPnl = currentValue - (pnl.unrealizedCost || 0); // both shekels ✓
  const unrealizedRoiPct = pnl.unrealizedCost > 0 ? (unrealizedPnl / pnl.unrealizedCost) * 100 : 0;

  const ms = live.market_state;
  const isRegular = !ms || ms === 'REGULAR';
  const hasPre  = live.pre_market_price  != null;
  const hasPost = live.post_market_price != null;
  const showExtended = !isRegular && (hasPre || hasPost);
  // Show pre-market only when an actual pre-market print exists; otherwise fall
  // back to the LAST after-hours price (e.g. PREPRE / CLOSED overnight) so the
  // last extended-hours quote stays visible even when no session is live.
  const usePre = (ms === 'PRE' || ms === 'PREPRE') && hasPre;
  const extPrice     = usePre ? live.pre_market_price          : live.post_market_price;
  const extChg       = usePre ? live.pre_market_change         : live.post_market_change;
  const extChgPct    = usePre ? live.pre_market_change_percent : live.post_market_change_percent;
  const extTime      = usePre ? live.pre_market_time           : live.post_market_time;
  const extLabel     = usePre ? '🌅 טרום מסחר' : '🌙 אחרי מסחר';
  const stateLabel   = { REGULAR: null, PRE: 'טרום מסחר', PREPRE: 'טרום מסחר', POST: 'אחמ"ש', POSTPOST: 'אחמ"ש', CLOSED: 'סגור' }[ms] || ms;

  return (
    <div className="pnl-item pnl-live pnl-span2">
      <div className="stat-label" style={{display:'flex', alignItems:'center', gap:8}}>
        סטטוס חי
        {stateLabel && (
          <span style={{
            fontSize: '.68rem', fontWeight: 700, padding: '1px 7px', borderRadius: 10,
            background: isRegular ? 'var(--green)' : 'var(--accent-light)',
            color: isRegular ? '#fff' : 'var(--accent)',
            border: '1px solid currentColor'
          }}>{stateLabel}</span>
        )}
      </div>
      <div className="pnl-rows3" style={{gap: '10px'}}>
          <div className="pnl-line">
              <span>מחיר נוכחי (מסחר סדיר)</span>
              <span className="pnl-line-val">{fmtPrice(live.current, instance.base_currency)}
                  <span className={`live-change ${live.change >= 0 ? 'green' : 'red'}`}>
                      ({live.change_percent.toFixed(2)}% יומי)
                  </span>
              </span>
          </div>
          {showExtended && extPrice != null && (
            <div className="pnl-line" style={{
              background: 'var(--surface2)', borderRadius: 8, padding: '6px 10px',
              border: '1px solid var(--border)'
            }}>
              <span style={{fontWeight:700}}>{extLabel}</span>
              <span className={`pnl-line-val ${(extChgPct ?? 0) >= 0 ? 'green' : 'red'}`}>
                {fmtPrice(extPrice, instance.base_currency)}
                {extChgPct != null && (
                  <span className="live-change">
                    ({extChgPct >= 0 ? '+' : ''}{extChgPct.toFixed(2)}%)
                  </span>
                )}
                {extTime && (
                  <span style={{fontSize:'.65rem', color:'var(--muted)', marginRight:4, display:'block'}}>
                    עדכון: {new Date(extTime).toLocaleTimeString('he-IL', {hour:'2-digit', minute:'2-digit'})}
                  </span>
                )}
              </span>
            </div>
          )}
          <div className="pnl-line">
              <span>שווי החזקות</span>
              <span className="pnl-line-val">{fmt(currentValue, instance.base_currency)}</span>
          </div>
          <div className="pnl-line pnl-line-total">
              <span>רווח/הפסד נוכחי</span>
              <span className={`pnl-line-val ${unrealizedPnl >= 0 ? 'green' : 'red'}`}>
                  {fmt(unrealizedPnl, instance.base_currency)}
                  <span className={`live-change ${unrealizedRoiPct >= 0 ? 'green' : 'red'}`}>
                      ({fmtPct(unrealizedRoiPct)})
                  </span>
              </span>
          </div>
      </div>
    </div>
  );
}

export default function InstancePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('transactions');
  const [modal, setModal] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertForm, setAlertForm] = useState({ type: 'price_above', threshold: '', note: '' });
  const [alertFormOpen, setAlertFormOpen] = useState(false);
  const [alertSaving, setAlertSaving] = useState(false);

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try { const { data } = await api.get(`/instances/${id}/alerts`); setAlerts(data); }
    catch (e) { console.error('alerts load:', e); }
    finally { setAlertsLoading(false); }
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/instances/${id}`);
      setInst(data);
    } catch {
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async () => {
    if (inst.status === 'open') {
      // User wants to close the position
      const pnl = inst.pnl || {};
      const remainingQty = pnl.remainingQty || 0;
      
      if (remainingQty > 0) {
        alert('לא ניתן לסגור פוזיציה שיש בה עדיין מניות. יש למכור את כל ההחזקות תחילה.');
        return;
      }

      if (window.confirm('האם אתה בטוח שברצונך לסגור את הפוזיציה? פעולה זו אינה הפיכה.')) {
        await api.patch(`/instances/${id}/close`);
        load();
      }
    } else {
      // User wants to reopen
      await api.patch(`/instances/${id}/reopen`);
      load();
    }
  };

  const deleteTransaction = async (txId) => {
    if (!window.confirm('למחוק תנועה זו?')) return;
    await api.delete(`/instances/${id}/transactions/${txId}`);
    load();
  };

  const deleteCommission = async (cId) => {
    if (!window.confirm('למחוק עמלה זו?')) return;
    await api.delete(`/instances/${id}/commissions/${cId}`);
    load();
  };

  const deleteDividend = async (dId) => {
    if (!window.confirm('למחוק דיבידנד זה?')) return;
    await api.delete(`/instances/${id}/dividends/${dId}`);
    load();
  };

  if (loading) return <div className="loading">טוען...</div>;
  if (!inst) return null;

  const pnl = inst.pnl || {};
  const profit = pnl.netProfit || 0;
  const grossRoi = pnl.totalBoughtCost > 0 ? ((pnl.grossProfit || 0) / pnl.totalBoughtCost) * 100 : 0;
  const stillInside = Math.max(0, (pnl.totalBoughtCost || 0) - (pnl.totalSoldRevenue || 0) - (pnl.totalDividends || 0));
  const taxAmount = (inst.commissions || []).filter(c => c.type === 'tax').reduce((s, c) => s + c.amount, 0);
  const otherCommissions = (pnl.totalCommissions || 0) - taxAmount;

  const commissionLabel = (type) => COMMISSION_TYPES.find(t => t.value === type)?.label || type;

  return (
    <div className="page">
      {/* Header */}
      <div className="inst-header">
        <div className="inst-header-top">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            דשבורד
          </button>
          <button className="btn btn-secondary btn-sm" onClick={toggleStatus}>
            {inst.status === 'open' ? '🔒 סגור פוזיציה' : '🔓 פתח מחדש'}
          </button>
        </div>
        <div className="inst-title-row">
          <div>
            <h1 className="inst-symbol">{inst.stock_symbol}</h1>
            <div className="inst-subtitle">{inst.platform_name}</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setModal('edit')}>ערוך</button>
        </div>
        <div className="inst-badges">
            {inst.ticker_symbol && <span className="tag" style={{background: 'var(--accent)', color: '#fff', fontWeight: 700}}>{inst.ticker_symbol}</span>}
            <span className={`badge ${inst.status}`}>{inst.status === 'open' ? 'פתוחה' : 'סגורה'}</span>
            <span className="tag">{inst.base_currency}</span>
        </div>
      </div>

      {/* P&L Summary */}
      <div className="pnl-banner">
        <div className="pnl-banner-title">📊 סיכום רווח/הפסד</div>
        <div className="pnl-grid">

          {/* שורה 1: תשואה — רוחב מלא */}
          <div className="pnl-item pnl-main pnl-span2">
            <div className="stat-label">תשואה</div>
            <div className="pnl-two-rows">
              <div className="pnl-roi-row">
                <span className="pnl-roi-label">ברוטו</span>
                <span className={`pnl-roi-amount ${(pnl.grossProfit||0) >= 0 ? 'green' : 'red'}`}>{fmt(pnl.grossProfit, inst.base_currency)}</span>
                <span className={`pnl-roi-pct ${grossRoi >= 0 ? 'green' : 'red'}`}>{fmtPct(grossRoi)}</span>
              </div>
              <div className="pnl-roi-row">
                <span className="pnl-roi-label">נטו</span>
                <span className={`pnl-roi-amount ${profit >= 0 ? 'green' : 'red'}`}>{fmt(profit, inst.base_currency)}</span>
                <span className={`pnl-roi-pct ${(pnl.roi||0) >= 0 ? 'green' : 'red'}`}>{fmtPct(pnl.roi)}</span>
              </div>
            </div>
          </div>

          {/* שורה 2: סה"כ השקעה + הוצאות */}
          <div className="pnl-item">
            <div className="stat-label pnl-section-title">סה"כ השקעה</div>
            <div className="pnl-rows3">
              <div className="pnl-line"><span>סכום</span><span className="pnl-line-val">{fmt(pnl.totalBoughtCost, inst.base_currency)}</span></div>
              <div className="pnl-line"><span>מניות</span><span className="pnl-line-val">{parseFloat((pnl.totalBoughtQty || 0).toFixed(2))}</span></div>
              <div className="pnl-line"><span>מחיר ממוצע</span><span className="pnl-line-val">{fmtPrice(pnl.avgBuyPrice, inst.base_currency)}</span></div>
            </div>
          </div>

          <div className="pnl-item">
            <div className="stat-label pnl-section-title">הוצאות</div>
            <div className="pnl-rows3">
              <div className="pnl-line"><span>עמלות</span><span className="pnl-line-val red">{fmt(otherCommissions, inst.base_currency)}</span></div>
              <div className="pnl-line"><span>מיסים</span><span className="pnl-line-val red">{fmt(taxAmount, inst.base_currency)}</span></div>
              <div className="pnl-line pnl-line-total"><span>סה"כ הוצאות</span><span className="pnl-line-val red">{fmt(pnl.totalCommissions, inst.base_currency)}</span></div>
            </div>
          </div>

          {/* שורה 3: סה"כ נמכר + סטטוס */}
          <div className="pnl-item">
            <div className="stat-label pnl-section-title">סה"כ נמכר</div>
            <div className="pnl-rows3">
              <div className="pnl-line"><span>ממכירות</span><span className="pnl-line-val">{fmt(pnl.totalSoldRevenue, inst.base_currency)}</span></div>
              {(pnl.totalDividends > 0) && (
                <div className="pnl-line"><span>💰 דיבידנדים</span><span className="pnl-line-val green">+{fmt(pnl.totalDividends, inst.base_currency)}</span></div>
              )}
              {(pnl.totalDividends > 0) && (
                <div className="pnl-line pnl-line-total"><span>סה"כ</span><span className="pnl-line-val">{fmt((pnl.totalSoldRevenue||0)+(pnl.totalDividends||0), inst.base_currency)}</span></div>
              )}
              <div className="pnl-line"><span>מניות שנמכרו</span><span className="pnl-line-val">{parseFloat((pnl.totalSoldQty || 0).toFixed(2))}</span></div>
            </div>
          </div>

          <div className="pnl-item">
            <div className="stat-label pnl-section-title">סטטוס עכשווי</div>
            <div className="pnl-rows3">
              <div className="pnl-line"><span>כסף בפנים</span><span className={`pnl-line-val ${stillInside > 0 ? 'accent' : ''}`}>{stillInside > 0 ? fmt(stillInside, inst.base_currency) : '-'}</span></div>
              <div className="pnl-line"><span>מניות בהחזקה</span><span className="pnl-line-val">{parseFloat((pnl.remainingQty || 0).toFixed(2))}</span></div>
            </div>
          </div>
          
          {/* Live Data */}
          <LiveData instance={inst} />

        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${tab === 'transactions' ? 'active' : ''}`} onClick={() => setTab('transactions')}>
          תנועות ({inst.transactions?.length || 0})
        </button>
        <button className={`tab ${tab === 'commissions' ? 'active' : ''}`} onClick={() => setTab('commissions')}>
          עמלות ({inst.commissions?.length || 0})
        </button>
        <button className={`tab ${tab === 'dividends' ? 'active' : ''}`} onClick={() => setTab('dividends')}>
          💰 דיבידנדים ({inst.dividends?.length || 0})
        </button>
        <button className={`tab ${tab === 'conversions' ? 'active' : ''}`} onClick={() => setTab('conversions')}>
          המרות ({inst.conversions?.length || 0})
        </button>
        <button className={`tab ${tab === 'alerts' ? 'active' : ''}`} onClick={() => { setTab('alerts'); loadAlerts(); }}>
          🔔 התראות{alerts.length > 0 && <span className="tab-count">{alerts.length}</span>}
        </button>
      </div>

      {/* Transactions */}
      {tab === 'transactions' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('transaction')}>+ הוסף תנועה</button>
          </div>
          {!inst.transactions?.length ? (
            <div className="empty"><div className="empty-icon">📋</div><div className="empty-title">אין תנועות עדיין</div></div>
          ) : (
            inst.transactions.map(tx => (
              <div key={tx.id} className={`tx-card ${tx.type}`}>
                <div className="tx-card-main">
                  <span className={`tx-type ${tx.type === 'buy' ? 'profit' : 'loss'}`}>
                    {tx.type === 'buy' ? '🟢 קנייה' : '🔴 מכירה'}
                  </span>
                  <strong className="tx-total">{tx.currency === 'ILS' ? fmt(Math.round(tx.quantity * tx.price_per_share / 100), tx.currency) : fmt(tx.quantity * tx.price_per_share, tx.currency)}</strong>
                  <button className="btn-icon" onClick={() => { setEditTx(tx); setModal('editTransaction'); }}>✏️</button>
                  <button className="btn-icon tx-delete" onClick={() => deleteTransaction(tx.id)}>🗑️</button>
                </div>
                <div className="tx-card-details">
                  <span>{parseFloat(tx.quantity.toFixed(2))} יח' × {fmtPrice(tx.price_per_share, tx.currency)}{tx.currency === 'ILS' ? ` = ${fmt(Math.round(tx.quantity * tx.price_per_share / 100), tx.currency)}` : ''}</span>
                  <span style={{color:'var(--muted)'}}>{new Date(tx.transaction_date).toLocaleDateString('he-IL')}</span>
                  {tx.notes && <span style={{color:'var(--muted)', fontSize:'.8rem'}}>{tx.notes}</span>}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Commissions */}
      {tab === 'commissions' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('commission')}>+ הוסף עמלה</button>
          </div>
          {!inst.commissions?.length ? (
            <div className="empty"><div className="empty-icon">💸</div><div className="empty-title">אין עמלות רשומות</div></div>
          ) : (
            inst.commissions.map(c => {
              const isCredit = c.amount < 0;
              return (
                <div key={c.id} className="tx-card">
                  <div className="tx-card-main">
                    <span className="tx-type">
                      {isCredit ? '✅ זיכוי — ' : ''}{commissionLabel(c.type)}
                    </span>
                    <strong className={`tx-total ${isCredit ? 'profit' : 'loss'}`}>{fmt(c.amount, c.currency)}</strong>
                    <button className="btn-icon" onClick={() => { setEditItem(c); setModal('editCommission'); }}>✏️</button>
                    <button className="btn-icon tx-delete" onClick={() => deleteCommission(c.id)}>🗑️</button>
                  </div>
                  <div className="tx-card-details">
                    {c.description
                      ? <span style={{color:'var(--muted)'}}>{c.description}</span>
                      : <span style={{color:'var(--muted)', opacity:.4}}>אין תיאור</span>
                    }
                    <span style={{color:'var(--muted)'}}>{new Date(c.commission_date).toLocaleDateString('he-IL')}</span>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      {/* Dividends */}
      {tab === 'dividends' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('dividend')}>+ דיבידנד חדש</button>
          </div>
          {!inst.dividends?.length ? (
            <div className="empty"><div className="empty-icon">💰</div><div className="empty-title">אין דיבידנדים רשומים</div></div>
          ) : (
            inst.dividends.map(d => (
              <div key={d.id} className="tx-card">
                <div className="tx-card-main">
                  <span className="tx-type profit">💰 דיבידנד</span>
                  <strong className="tx-total green">{fmt(d.total_amount, d.currency)}</strong>
                  <button className="btn-icon" onClick={() => { setEditItem(d); setModal('editDividend'); }}>✏️</button>
                  <button className="btn-icon tx-delete" onClick={() => deleteDividend(d.id)}>🗑️</button>
                </div>
                <div className="tx-card-details">
                  {d.amount_per_share && d.quantity && (
                    <span>{d.quantity} יח' × {fmtPrice(d.amount_per_share, d.currency)}</span>
                  )}
                  <span style={{color:'var(--muted)'}}>{new Date(d.dividend_date).toLocaleDateString('he-IL')}</span>
                  {d.notes && <span style={{color:'var(--muted)', fontSize:'.8rem'}}>{d.notes}</span>}
                </div>
              </div>
            ))
          )}
          {inst.dividends?.length > 0 && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginTop: 12, fontSize: '.9rem' }}>
              סה"כ דיבידנדים: <strong className="green">{fmt(inst.pnl?.totalDividends, inst.base_currency)}</strong>
            </div>
          )}
        </>
      )}

      {/* Conversions */}
      {tab === 'conversions' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('conversion')}>+ הוסף המרה</button>
          </div>
          {!inst.conversions?.length ? (
            <div className="empty"><div className="empty-icon">💱</div><div className="empty-title">אין המרות רשומות</div></div>
          ) : (
            inst.conversions.map(c => (
              <div key={c.id} className="tx-card">
                <div className="tx-card-main">
                  <span style={{fontWeight:700}}>{c.from_currency} → {c.to_currency}</span>
                  <strong className="tx-total">{fmt(c.amount_to, c.to_currency)}</strong>
                  <button className="btn-icon" onClick={() => { setEditItem(c); setModal('editConversion'); }}>✏️</button>
                </div>
                <div className="tx-card-details">
                  <span style={{color:'var(--muted)'}}>מקור: {fmt(c.amount_from, c.from_currency)}</span>
                  <span style={{color:'var(--muted)'}}>שער: {c.exchange_rate}</span>
                  <span style={{color:'var(--muted)'}}>{new Date(c.conversion_date).toLocaleDateString('he-IL')}</span>
                  {c.notes && <span style={{color:'var(--muted)', fontSize:'.8rem'}}>{c.notes}</span>}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {tab === 'alerts' && (
        <>
          {inst.ticker_symbol ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>התראות נשלחות לווצאפ · cooldown 4שע</span>
                <button className="btn btn-primary btn-sm" onClick={() => setAlertFormOpen(v => !v)}>+ התראה חדשה</button>
              </div>

              {alertFormOpen && (
                <div className="card" style={{ marginBottom: 14 }}>
                  <div className="card-title">הגדרת התראה — {inst.ticker_symbol}</div>
                  <div className="form-group">
                    <label className="form-label">סוג התראה</label>
                    <select className="form-select" value={alertForm.type} onChange={e => setAlertForm(f => ({...f, type: e.target.value}))}>
                      <option value="price_above">📈 מחיר עולה מעל...</option>
                      <option value="price_below">📉 מחיר יורד מתחת ל...</option>
                      <option value="change_pct_up">🔼 שינוי יומי חיובי של...</option>
                      <option value="change_pct_down">🔽 שינוי יומי שלילי של...</option>
                    </select>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        {alertForm.type.startsWith('change') ? 'אחוז (%)שינוי' : inst.base_currency === 'ILS' ? "מחיר יעד (אג')" : `מחיר יעד (${inst.base_currency})`}
                      </label>
                      <input className="form-input" type="number" step="any" min="0"
                        placeholder={alertForm.type.startsWith('change') ? 'לדוגמה: 5' : inst.base_currency === 'ILS' ? 'לדוגמא: 1850' : 'לדוגמא: 18.00'}
                        value={alertForm.threshold}
                        onChange={e => setAlertForm(f => ({...f, threshold: e.target.value}))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">הערה (אופציונלי)</label>
                      <input className="form-input" placeholder="לדוגמא: יעד מכירה"
                        value={alertForm.note}
                        onChange={e => setAlertForm(f => ({...f, note: e.target.value}))} />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button className="btn btn-primary" style={{ flex: 1 }} disabled={alertSaving || !alertForm.threshold}
                      onClick={async () => {
                        setAlertSaving(true);
                        try {
                          await api.post(`/instances/${id}/alerts`, alertForm);
                          setAlertFormOpen(false);
                          setAlertForm({ type: 'price_above', threshold: '', note: '' });
                          loadAlerts();
                        } finally { setAlertSaving(false); }
                      }}>
                      {alertSaving ? 'שומר...' : 'צור התראה'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setAlertFormOpen(false)}>ביטול</button>
                  </div>
                </div>
              )}

              {alertsLoading ? (
                <div className="loading">טוען...</div>
              ) : alerts.length === 0 ? (
                <div className="empty"><div className="empty-icon">🔔</div><div className="empty-title">אין התראות מוגדרות</div><div className="empty-sub">הגדר התראת מחיר או שינוי אחוזי</div></div>
              ) : (
                alerts.map(a => {
                  const typeLabel = { price_above: '📈 מחיר מעל', price_below: '📉 מחיר מתחת ל-', change_pct_up: '🔼 עלייה יומית', change_pct_down: '🔽 ירידה יומית' };
                  const isChange = a.type.startsWith('change');
                  return (
                    <div key={a.id} className="tx-card" style={{ opacity: a.enabled ? 1 : 0.5 }}>
                      <div className="tx-card-main">
                        <span style={{ fontWeight: 700, color: a.enabled ? 'var(--text)' : 'var(--muted)' }}>
                          {typeLabel[a.type]} {isChange ? `${a.threshold}%` : inst.base_currency === 'ILS' ? `${a.threshold} אג'` : `${a.threshold} ${inst.base_currency}`}
                        </span>
                        <div style={{ display: 'flex', gap: 6, marginRight: 'auto' }}>
                          <button className="btn btn-secondary btn-sm" onClick={async () => {
                            await api.patch(`/alerts/${a.id}/toggle`);
                            loadAlerts();
                          }}>{a.enabled ? '⏸ כבה' : '▶ הפעל'}</button>
                          <button className="btn-icon" onClick={async () => { await api.delete(`/alerts/${a.id}`); loadAlerts(); }}>🗑️</button>
                        </div>
                      </div>
                      <div className="tx-card-details">
                        {a.note && <span style={{ color: 'var(--muted)' }}>📝 {a.note}</span>}
                        {a.last_triggered_at
                          ? <span style={{ color: 'var(--muted)' }}>הופעלה: {new Date(a.last_triggered_at).toLocaleDateString('he-IL', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}</span>
                          : <span style={{ color: 'var(--muted)', opacity: .5 }}>טרם הופעלה</span>
                        }
                      </div>
                    </div>
                  );
                })
              )}
            </>
          ) : (
            <div className="empty">
              <div className="empty-icon">⚠️</div>
              <div className="empty-title">אין טיקר מוגדר לפוזיציה זו</div>
              <div className="empty-sub">הוסף טיקר בעריכת הפוזיציה כדי להפעיל התראות</div>
            </div>
          )}
        </>
      )}

      {modal === 'transaction' && <TransactionModal instance={inst} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === 'editTransaction' && <TransactionModal instance={inst} editTx={editTx} onClose={() => { setModal(null); setEditTx(null); }} onSaved={() => { setModal(null); setEditTx(null); load(); }} />}
      {modal === 'commission' && <CommissionModal instance={inst} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === 'editCommission' && <CommissionModal instance={inst} editCommission={editItem} onClose={() => { setModal(null); setEditItem(null); }} onSaved={() => { setModal(null); setEditItem(null); load(); }} />}
      {modal === 'conversion' && <ConversionModal instance={inst} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === 'editConversion' && <ConversionModal instance={inst} editConversion={editItem} onClose={() => { setModal(null); setEditItem(null); }} onSaved={() => { setModal(null); setEditItem(null); load(); }} />}
      {modal === 'dividend' && <DividendModal instance={inst} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === 'editDividend' && <DividendModal instance={inst} editDividend={editItem} onClose={() => { setModal(null); setEditItem(null); }} onSaved={() => { setModal(null); setEditItem(null); load(); }} />}
      {modal === 'edit' && <EditInstanceModal instance={inst} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
